import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';

import { toDate } from 'shared/utilities';
import { useBoundingClientRect, useObservable } from 'shared/hooks';
import { UiIcon } from 'shared/uikit-new';
import { DateType } from 'shared/types';

import { Store } from 'pages/calendar/store';
import { CALENDAR_PAGE } from 'pages/calendar/constants/CALENDAR_PAGE';

import { CalendarPageMonthCalendarItem } from 'pages/calendar/components/CalendarPageMonthCalendarItem';
import { CalendarPageCreatingPopout } from 'pages/calendar/components/CalendarPageCreatingPopout';
import { CalendarPageDayPreview } from 'pages/calendar/components/CalendarPageDayPreview';

import './styles.scss';

type PropsType = {
    date: DateType;
};
export const CalendarPageMonthDay = observer(function CalendarPageMonthDay({ date }: PropsType) {
    const ref = useRef<HTMLDivElement>(null);

    const boundingClientRect = useBoundingClientRect(ref);

    const store = useObservable({
        fitting: 0,
        isOpened: false,
        isRendered: false,
        calendarPageDayPreviewBounds: null as null | DOMRect,
    });

    const now = toDate();

    const calendarItems = Store.calendarItems.filter(calendarItem => {
        return toDate(calendarItem.startAt).isSame(date, 'day');
    });

    useEffect(() => {
        if (boundingClientRect.height > 0) {
            //fix flicking on no weekends
            store.set('fitting', Math.floor((boundingClientRect.height - 32) / 15));
            store.set('isRendered', true);
        }
    }, [boundingClientRect, store]);

    return (
        <CalendarPageCreatingPopout
            date={date.set('hour', CALENDAR_PAGE.HOURS[0]).set('minute', 0)}
            className={classnames('calendar-page-month-day', {
                'calendar-page-month-day--weekend': date.day() === 0 || date.day() === 6,
                'calendar-page-month-day--today': date.isSame(now, 'day'),
                'calendar-page-month-day--outer': !date.isSame(Store.date, 'month'),
            })}
            render={({ onClick }) => (
                <div ref={ref} className='calendar-page-month-day__inner'>
                    <div className='calendar-page-month-day__overlay' onClick={onClick} />
                    <div className='calendar-page-month-day__header'>
                        <div
                            className='calendar-page-month-day__date'
                            onClick={() =>
                                Store.update({
                                    view: 'day',
                                    date,
                                })
                            }
                        >
                            {date.format('D')}
                        </div>
                        <div className='calendar-page-month-day__create' onClick={onClick}>
                            <UiIcon.Plus size={16} />
                        </div>
                    </div>
                    <div className='calendar-page-month-day__items'>
                        {store.isRendered && (
                            <>
                                {calendarItems.slice(0, calendarItems.length > store.fitting ? store.fitting - 1 : 999).map(calendarItem => {
                                    return <CalendarPageMonthCalendarItem key={calendarItem.id} calendarItem={calendarItem} />;
                                })}
                                {calendarItems.length > 0 && calendarItems.length > store.fitting && (
                                    <div
                                        className='calendar-page-month-day__more'
                                        onClick={e => {
                                            if(store.isOpened){
                                                store.set('isOpened', false);
                                                return;
                                            }
                                            if (!ref?.current) {
                                                return;
                                            }
                                            store.set('calendarPageDayPreviewBounds', ref.current.getBoundingClientRect());
                                            setTimeout(() => {
                                                store.set('isOpened', true);
                                            }, 10);
                                        }}
                                    >
                                        Ещё {calendarItems.length - store.fitting + 1}
                                    </div>
                                )}
                            </>
                        )}
                        <CalendarPageDayPreview
                            key={date.format()}
                            isOpened={store.isOpened}
                            onClose={() => {
                                store.set('isOpened', false);
                            }}
                            date={date}
                            bounds={store.calendarPageDayPreviewBounds}
                        />
                    </div>
                </div>
            )}
        />
    );
});
